import { Injectable, OnDestroy } from '@angular/core';
import { OnFieldChanged } from '../../../../../../shared/modules/configurable-form/models/on-field-changed';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { SpecialInstructionSetAction } from '../../../actions/special-instructions-actions/special-instructions.actions';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';

/**
 * Sets up a Subject to monitor the debounced input to the field, and to dispatch that
 * information to the Store as the payload of a new Action.
 *
 * Rather than unsubscribing directly at the end of the class' lifespan, it uses a
 * second Subject which exists solely to tell the debounce-dispatcher to stop taking
 * values, and so to shut itself down.
 */
@Injectable()
export class SpecialInstructionsSetCommand implements OnFieldChanged, OnDestroy {
  private textCache: string;
  private debounceSubject = new Subject();
  private _destroyed$ = new Subject();
  /**
   * Milliseconds to await new input during a debounce
   * @type {number}
   */
  public debounceMS = 350;

  constructor(private store: Store<OrderBuilderState>) {
    this.debounceDispatcher();
  }

  onFieldChanged(text: string): void {
    this.debounceSubject.next(text);
  }

  debounceDispatcher(): Subscription {
    return this.debounceSubject
      .pipe(
        debounceTime(this.debounceMS),
        takeUntil(this._destroyed$),
      )
      .subscribe((text: string) => {
        if (text !== this.textCache) {
          this.store.dispatch(new SpecialInstructionSetAction(text));
        }

        this.textCache = text;
      });
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
